import { useEffect, useRef, useState } from "react";
import { ArrowRight, Upload, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import api from "@/lib/api";
import { PACKAGES, FIRESIDE, INDIVIDUAL } from "./corporateData";

const inputCls = "w-full bg-transparent border-0 border-b border-zinc-700 rounded-none px-0 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-[#C87455] transition-colors font-mono-tech text-sm";
const labelCls = "font-mono-tech text-[10px] uppercase tracking-[0.3em] text-zinc-500 block mb-1";

const FIRESIDE_CHOICES = [
  ["box", "Box engraving only — included"], ["smoker", "Add smoker top-hat engraving — $15"], ["stones", "Add whiskey-stone engraving — $20"],
  ["full", "Fully Personalized upgrade — $30"], ["other", "Other customization — request pricing"],
];

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`text-left border px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.15em] transition-colors ${on ? "border-[#C87455] bg-[#C87455]/10 text-[#C87455]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
    {children}
  </button>
);

export const CorporateQuoteForm = ({ preset }) => {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", budget: "", date: "", delivery: "shipping", postal: "", drinkware: "", names: "yes", card: "", notes: "" });
  const [pkgs, setPkgs] = useState(() => (preset ? { [preset]: "" } : {}));
  const [fireside, setFireside] = useState(preset === "fireside" ? "box" : "");
  const [individual, setIndividual] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    const h = (e) => {
      const id = e.detail;
      if (!id) return;
      setPkgs((p) => (id in p ? p : { ...p, [id]: "" }));
      if (id === "fireside") setFireside((v) => v || "box");
    };
    window.addEventListener("corp-preset", h);
    return () => window.removeEventListener("corp-preset", h);
  }, []);

  useEffect(() => {
    if (done) setTimeout(() => document.getElementById("corporate-quote")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [done]);
  const togglePkg = (id) => setPkgs((p) => { const n = { ...p }; if (id in n) delete n[id]; else n[id] = ""; return n; });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.date) return toast.error("Name, email and your required in-hand date are required.");
    if (!Object.keys(pkgs).length && !individual.length) return toast.error("Select at least one package or individual gift.");
    setSending(true);
    const all = [...PACKAGES, FIRESIDE];
    const lines = [
      "CORPORATE QUOTE REQUEST (not a stock reservation)",
      ...Object.entries(pkgs).map(([id, q]) => `• ${all.find((p) => p.id === id)?.name}: qty ${q || "TBC"}${id === "fireside" && fireside ? ` — ${FIRESIDE_CHOICES.find((c) => c[0] === fireside)?.[1]}` : ""}`),
      individual.length ? `Individual gifts: ${individual.join("; ")}` : "",
      `Budget per recipient: ${f.budget || "—"}`, `Required in-hand date: ${f.date}`,
      `Delivery: Shipping — postal code ${f.postal || "TBC"}`,
      `Preferred drinkware model/colour: ${f.drinkware || "—"}`, `Recipient-name personalization: ${f.names}`,
      `Greeting-card message: ${f.card || "—"}`, f.notes ? `Additional requests: ${f.notes}` : "",
    ].filter(Boolean);
    try {
      await api.post("/quotes", {
        name: f.company ? `${f.name} — ${f.company}` : f.name, email: f.email, phone: f.phone, project_type: "Corporate gifting",
        material: "", quantity: Object.values(pkgs).filter(Boolean).join(" / "), description: lines.join("\n"),
        attachment_file_id: file?.file_id || "", attachment_filename: file?.filename || "",
      });
      setDone(true);
      track("corporate_quote_submitted", { packages: Object.keys(pkgs).join(" / ") });
    } catch {
      toast.error("Could not send — please try again.");
    }
    setSending(false);
  };

  if (done) {
    return (
      <div className="border border-[#C87455]/50 bg-[#C87455]/5 p-8 md:p-10" data-testid="corp-quote-success">
        <Check size={20} className="text-[#C87455]" />
        <p className="font-serif-display text-2xl md:text-3xl text-white mt-4 leading-snug">Thank you for contacting Ember &amp; Steel Studio. We'll review your selections, availability and required date, then follow up with a tailored quotation.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-10" data-testid="corp-quote-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-7">
        {[["name", "Contact name *", "Jordan Lee"], ["company", "Company name", "Acme Energy Ltd."], ["email", "Email *", "jordan@acme.ca"], ["phone", "Phone (optional)", "(780) 000-0000"]].map(([k, l, ph]) => (
          <label key={k}><span className={labelCls}>{l}</span><input data-testid={`corp-${k}-input`} type={k === "email" ? "email" : "text"} value={f[k]} onChange={set(k)} placeholder={ph} className={inputCls} /></label>
        ))}
      </div>

      <div>
        <span className={labelCls}>Package selection — choose any, then add quantities</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          {[...PACKAGES, FIRESIDE].map((p) => (
            <div key={p.id} className={`border p-4 transition-colors ${p.id in pkgs ? "border-[#C87455]/60 bg-[#C87455]/5" : "border-zinc-800"}`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" data-testid={`corp-pkg-${p.id}`} checked={p.id in pkgs} onChange={() => togglePkg(p.id)} className="mt-1 accent-[#C87455]" />
                <span className="flex-1">
                  <span className="text-sm text-white block">{p.id === "fireside" ? "Fireside Cocktail Smoker Set" : p.name}</span>
                  <span className="font-mono-tech text-[10px] text-zinc-500">from ${p.price} CAD</span>
                </span>
              </label>
              {p.id in pkgs && (
                <div className="mt-3 pl-7 space-y-3">
                  <input data-testid={`corp-qty-${p.id}`} type="number" min="1" placeholder="Quantity" value={pkgs[p.id]} onChange={(e) => setPkgs({ ...pkgs, [p.id]: e.target.value })} className={`${inputCls} py-2`} />
                  {p.id === "fireside" && (
                    <div className="grid gap-2">
                      {FIRESIDE_CHOICES.map(([k, l]) => (
                        <Chip key={k} testid={`corp-fireside-${k}`} on={fireside === k} onClick={() => setFireside(k)}>{l}</Chip>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <span className={labelCls}>Individual-product enquiries (optional)</span>
        <div className="flex flex-wrap gap-2 mt-3">
          {INDIVIDUAL.map(([label]) => (
            <Chip key={label} testid={`corp-ind-${label.split(" ")[1]?.toLowerCase()}`} on={individual.includes(label)} onClick={() => setIndividual((s) => (s.includes(label) ? s.filter((x) => x !== label) : [...s, label]))}>{label}</Chip>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-7">
        <label><span className={labelCls}>Budget per recipient (optional)</span><input data-testid="corp-budget-input" value={f.budget} onChange={set("budget")} placeholder="$100–150" className={inputCls} /></label>
        <label><span className={labelCls}>Required in-hand date *</span><input data-testid="corp-date-input" type="date" min={new Date().toISOString().slice(0, 10)} value={f.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} /></label>
        <label><span className={labelCls}>Shipping postal code</span><input data-testid="corp-postal-input" value={f.postal} onChange={set("postal")} placeholder="T9E 0A1" className={inputCls} /></label>
        <label><span className={labelCls}>Preferred drinkware model & colour</span><input data-testid="corp-drinkware-input" value={f.drinkware} onChange={set("drinkware")} placeholder="e.g. YETI 20 oz, navy" className={inputCls} /></label>
        <div>
          <span className={labelCls}>Recipient-name personalization</span>
          <div className="flex gap-2 mt-2">
            <Chip testid="corp-names-yes" on={f.names === "yes"} onClick={() => setF({ ...f, names: "yes" })}>Yes</Chip>
            <Chip testid="corp-names-no" on={f.names === "no"} onClick={() => setF({ ...f, names: "no" })}>No</Chip>
          </div>
        </div>
        <label className="sm:col-span-2"><span className={labelCls}>Greeting-card message (optional)</span><input data-testid="corp-card-input" value={f.card} onChange={set("card")} placeholder="Thank you for a great year — The Acme Team" className={inputCls} /></label>
        <label className="sm:col-span-2"><span className={labelCls}>Additional requests</span><textarea data-testid="corp-notes-input" rows={3} value={f.notes} onChange={set("notes")} placeholder="Mixed packages, non-Christmas wording, extra engraving locations…" className={`${inputCls} resize-none font-sans`} /></label>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.pdf" onChange={upload} className="hidden" data-testid="corp-logo-input" />
        {file ? (
          <span className="inline-flex items-center gap-3 border border-[#C87455]/40 bg-[#C87455]/5 px-4 py-2 font-mono-tech text-xs text-zinc-300" data-testid="corp-logo-attached">
            <Upload size={12} className="text-[#C87455]" /> {file.filename}
            <button type="button" onClick={() => setFile(null)} className="text-zinc-500 hover:text-red-400"><X size={12} /></button>
          </span>
        ) : (
          <button type="button" data-testid="corp-logo-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-3 border border-dashed border-zinc-700 hover:border-[#C87455]/60 transition-colors px-4 py-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-400 disabled:opacity-60">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload logo
          </button>
        )}
        <button type="submit" data-testid="corp-submit-btn" disabled={sending || uploading} className="sm:ml-auto w-full sm:w-auto justify-center bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-xs inline-flex items-center gap-3 disabled:opacity-60">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <>Request a Corporate Quote <ArrowRight size={14} /></>}
        </button>
      </div>
      <p className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-wider">Selections are quote requests — not stock reservations or delivery commitments. Prices CAD, before GST and delivery.</p>
    </form>
  );
};
